import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext, StateToken, Store } from "@ngxs/store";
import { VsphereResourcesService } from "./vsphere-resources.service";
import {
    ClearVsphereResource,
    LoadAllVsphereResources,
    LoadDataTableVsphereResources,
    SetVsphereResource,
    UpdateCountersVsphereResources,
    LoadAllVsphereCharts,
    LoadVsphereChartsFilteredData
} from "./vsphere-resources.actions";
import { finalize, forkJoin, tap } from "rxjs";
import { VsphereResourcesStateModel, VsphereResource, VsphereCounterChartData } from "./vsphere-resources.model";
import { PreferencesFiltersState } from "src/app/store/preferences/filters/preferences-filters.state";
import { CompliantRatioService } from "src/app/shared/service/compliant-ratio.service";
import { VsphereResourceTypeEnum } from "src/app/shared/enum/vsphere-resource-type.enum";
import { ChartComplianceRatioItem } from "src/app/shared/component/chart-compliance-ratio/chart-compliance-ratio.component";
import { ChartComplianceTotalItem } from "src/app/shared/component/chart-compliance-total/chart-compliance-total.component";
import { ChartVersusItem } from "src/app/shared/component/chart-versus/chart-versus.component";
import { DateService } from "src/app/shared/service/date.service";
import { VsphereResourceTypeLabelPipe } from "src/app/shared/pipe/vsphere-resource-type-label.pipe";
import { ColorEnum } from "src/app/shared/enum/color.enum";
import { ItemTypeEnum } from "src/app/shared/enum/item-type.enum";
import { MetaService } from "src/app/store/meta/meta.service";
import { MetaRequestHelper } from "src/app/store/meta/meta-request.helper";
import { PreferencesProvidersState } from "src/app/store/preferences/providers/preferences-providers.state";
import { VsphereConfig } from "src/app/store/preferences/providers/preferences-providers.model";
import { PreferencesChartsState } from "src/app/store/preferences/charts/preferences-charts.state";

export const VSPHERE_RESOURCES_TOKEN: StateToken<VsphereResourcesStateModel> = new StateToken('vsphereResources');

const VsphereResourcesStateModelDefaults: VsphereResourcesStateModel = {
    resourcesPerServer: {},
    compliantRatio: '0%',
    totalItems: '0',
    compliantItems: '0',
    notCompliantItems: '0',
    datatable: [],
    selectedResource: {
        server: '',
        age: 0,
        id: '',
        tfstates: [],
        repositories: [],
        issues: [],
        name: '',
        source: '',
        state: 'ignored',
        type: ''
    },
    chartsDataPerServer: {},
    chartData: {
        filteredComplianceRatioChartData: {
            values: []
        },
        filteredComplianceTotalChartData: {
            values: []
        },
        filteredVersusChartData: {
            values: []
        }
    },
}

@State<VsphereResourcesStateModel>({
    name: VSPHERE_RESOURCES_TOKEN,
    defaults: VsphereResourcesStateModelDefaults
})

@Injectable()
export class VsphereResourcesState {

    private readonly KEY = VSPHERE_RESOURCES_TOKEN.getName()

    constructor(
        private vsphereResourcesService: VsphereResourcesService,
        private store: Store,
        private dateService: DateService,
        private vsphereResourceTypeLabelPipe: VsphereResourceTypeLabelPipe,
        private metaService: MetaService
    ) {
    }

    @Selector([VSPHERE_RESOURCES_TOKEN])
    static getResources(state: VsphereResourcesStateModel) {
        return (serverId:string) => state.resourcesPerServer[serverId]
    }

    @Selector([VSPHERE_RESOURCES_TOKEN])
    static getCompliantRatio(state: VsphereResourcesStateModel) {
        return state.compliantRatio
    }

    @Selector([VSPHERE_RESOURCES_TOKEN])
    static getTotalItems(state: VsphereResourcesStateModel) {
        return state.totalItems
    }

    @Selector([VSPHERE_RESOURCES_TOKEN])
    static getCompliantItems(state: VsphereResourcesStateModel) {
        return state.compliantItems
    }

    @Selector([VSPHERE_RESOURCES_TOKEN])
    static getNotCompliantItems(state: VsphereResourcesStateModel) {
        return state.notCompliantItems
    }

    @Selector([VSPHERE_RESOURCES_TOKEN])
    static getDatatable(state: VsphereResourcesStateModel) {
        return state.datatable
    }

    @Selector([VSPHERE_RESOURCES_TOKEN])
    static getSelectedResource(state: VsphereResourcesStateModel) {
        return state.selectedResource
    }

    @Selector([VSPHERE_RESOURCES_TOKEN])
    static getFilteredComplianceRatioChartData(state: VsphereResourcesStateModel) {
        return state.chartData.filteredComplianceRatioChartData
    }

    @Selector([VSPHERE_RESOURCES_TOKEN])
    static getFilteredComplianceTotalChartData(state: VsphereResourcesStateModel) {
        return state.chartData.filteredComplianceTotalChartData
    }

    @Selector([VSPHERE_RESOURCES_TOKEN])
    static getFilteredVersusChartData(state: VsphereResourcesStateModel) {
        return state.chartData.filteredVersusChartData
    }

    @Action(LoadAllVsphereResources)
    loadAllVsphereResources(ctx: StateContext<VsphereResourcesStateModel>) {
        const vsphereConfig = this.store.selectSnapshot(PreferencesProvidersState.getVsphereConfig);
        const serverIds = vsphereConfig.preferences && vsphereConfig.preferences.length > 0
            ? vsphereConfig.preferences
            : vsphereConfig.servers.map((s: any) => s.server_id);

        if (serverIds.length === 0) return;

        const staleServerIds = serverIds.filter((id: string) => this.metaService.isStale(this.KEY + "-server-" + id));

        if (staleServerIds.length === 0) return ctx.dispatch(new LoadDataTableVsphereResources());

        return forkJoin(
            staleServerIds.map((serverId: string) =>
                MetaRequestHelper.wrapRequest(
                    this.store,
                    this.KEY + "-server-" + serverId,
                    this.vsphereResourcesService.loadVsphereResources(serverId),
                    resources => {
                        ctx.patchState({
                            resourcesPerServer: {
                                ...ctx.getState().resourcesPerServer,
                                [serverId]: resources
                            }
                        })
                    }
                )
            )
        ).pipe(
            finalize(() => ctx.dispatch(new LoadDataTableVsphereResources()))
        );
    }

    @Action(LoadDataTableVsphereResources)
    loadDataTableVsphereResources(ctx: StateContext<VsphereResourcesStateModel>) {
        const state = ctx.getState()
        let datatable_resources: VsphereResource[] = []

        for (const [key, resources] of Object.entries(state.resourcesPerServer)) {
            resources.forEach((resource) => {
                datatable_resources.push(resource)
            })
        }

        let filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.VSPHERE_RESOURCES]]
        filters.forEach(filter => {
            if (filter.key == 'issuesCount') {
                datatable_resources = datatable_resources.filter((resource) => resource['issues'].length.toString() === filter.value)
            } else if (filter.key == 'issuesType') {
                datatable_resources = datatable_resources.filter((resource) => {
                    let issuesTypeFound = false
                    resource.issues.forEach(issue => {
                        issuesTypeFound = issuesTypeFound || filter.value.includes(issue.issue_type)
                    })
                    return issuesTypeFound
                })
            } else if (filter.key == 'age') {
                let unknownAge = 99999
                let filterAge: number = unknownAge
                if (filter.value == 'today') {
                    filterAge = 0
                } else if (filter.value.startsWith('last_')) {
                    filterAge = filter.value.substring(5, 7)
                }
                datatable_resources = datatable_resources.filter((resource) => {
                    let ageInRange = false
                    if (filterAge == unknownAge) {
                        ageInRange = resource.age == filterAge
                    } else {
                        ageInRange = resource.age <= filterAge
                    }
                    return ageInRange
                })
            } else {
                if (Array.isArray(filter.value)) {
                    datatable_resources = datatable_resources.filter((resource) => filter.value.includes(resource[filter.key as keyof VsphereResource]))
                } else {
                    datatable_resources = datatable_resources.filter((resource) => resource[filter.key as keyof VsphereResource] === filter.value)
                }
            }
        });
        ctx.patchState({
            datatable: datatable_resources
        })
        ctx.dispatch(new UpdateCountersVsphereResources())
    }

    @Action(UpdateCountersVsphereResources)
    updateCountersVsphereResources(ctx: StateContext<VsphereResourcesStateModel>) {
        const state = ctx.getState()
        let totalItemsCounter = 0
        let compliantItemsCounter = 0
        let notCompliantItemsCounter = 0
        state.datatable.forEach(resource => {
            if (resource.state == 'compliant') {
                totalItemsCounter++;
                compliantItemsCounter++;
            } else if (resource.state == 'not_compliant') {
                totalItemsCounter++;
                notCompliantItemsCounter++;
            }
        });
        ctx.patchState({
            compliantRatio: CompliantRatioService.calculateCompliantRatio(compliantItemsCounter, notCompliantItemsCounter),
            totalItems: '' + totalItemsCounter,
            compliantItems: '' + compliantItemsCounter,
            notCompliantItems: '' + notCompliantItemsCounter
        })
    }

    @Action(ClearVsphereResource)
    clearVsphereResource(ctx: StateContext<VsphereResourcesStateModel>) {
        return ctx.patchState({
            selectedResource: VsphereResourcesStateModelDefaults.selectedResource
        })
    }

    @Action(SetVsphereResource)
    setVsphereResource(ctx: StateContext<VsphereResourcesStateModel>, {resource_id, resource_server}: SetVsphereResource) {
        return this.vsphereResourcesService.loadVsphereResourceDetails(resource_server, resource_id)
            .pipe(
                tap(value => {
                    return ctx.patchState({
                        selectedResource: value
                    })
                }),
            )
    }

    @Action(LoadAllVsphereCharts)
    loadAllVsphereCharts(ctx: StateContext<VsphereResourcesStateModel>) {
        const chartsConfig = this.store.selectSnapshot(PreferencesChartsState.getConfig);
        const { start_date, end_date } = DateService.getChartsMeasuresDateRange(chartsConfig);
        const vsphereConfig = this.store.selectSnapshot(PreferencesProvidersState.getVsphereConfig);
        const serverIds = vsphereConfig.preferences && vsphereConfig.preferences.length > 0
            ? vsphereConfig.preferences
            : vsphereConfig.servers.map((s: any) => s.server_id);

        if (serverIds.length === 0) return;

        const staleServerIds = serverIds.filter((id: string) => this.metaService.isStale(this.KEY + "-charts-server-" + id + "-" + start_date + "-" + end_date + "-" + chartsConfig.granularity));

        if (staleServerIds.length === 0) return ctx.dispatch(new LoadVsphereChartsFilteredData());

        return forkJoin(
            staleServerIds.map((serverId: string) =>
                MetaRequestHelper.wrapRequest(
                    this.store,
                    this.KEY + "-charts-server-" + serverId + "-" + start_date + "-" + end_date + "-" + chartsConfig.granularity,
                    this.vsphereResourcesService.loadVsphereMeasures(serverId, start_date, end_date, chartsConfig.granularity),
                    charts_payload => {
                        ctx.patchState({
                            chartsDataPerServer: {
                                ...ctx.getState().chartsDataPerServer,
                                [serverId]: charts_payload.measures
                            }
                        })
                    }
                )
            )
        ).pipe(
            finalize(() => ctx.dispatch(new LoadVsphereChartsFilteredData()))
        );
    }

    mergeCountersByDate(input: { [server: string]: VsphereCounterChartData }): VsphereCounterChartData {
        const result: VsphereCounterChartData = {};

        for (const serverId of Object.keys(input)) {
            const dates = input[serverId];

            for (const dateKey of Object.keys(dates)) {
                const types = dates[dateKey];

                if (!result[dateKey]) {
                    result[dateKey] = {};
                }

                for (const typeKey of Object.keys(types)) {
                    if (!result[dateKey][typeKey]) {
                        result[dateKey][typeKey] = {
                            checked: 0,
                            unchecked: 0,
                            ignored: 0
                        };
                    }

                    result[dateKey][typeKey].checked += types[typeKey].checked;
                    result[dateKey][typeKey].unchecked += types[typeKey].unchecked;
                    result[dateKey][typeKey].ignored += types[typeKey].ignored;
                }
            }
        }

        return result;
    }

    @Action(LoadVsphereChartsFilteredData)
    loadVsphereChartsFilteredData(ctx: StateContext<VsphereResourcesStateModel>) {
        const state = ctx.getState()

        let complianceRatioChartValues: ChartComplianceRatioItem[] = [];
        let complianceTotalChartValues: ChartComplianceTotalItem[] = [];
        let complianceVersusChartValues: ChartVersusItem[] = [];
        let filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.VSPHERE_MEASURES]]
        let types: string[] = []
        let server_ids: string[] = []
        filters.forEach(filter => {
            if (filter.key === 'type') {
                types = filter.value
            } else if (filter.key === 'server') {
                server_ids = filter.value
            }
        })
        if (types.length == 0) {
            types = Object.keys(VsphereResourceTypeEnum)
        }

        let counterChartsData: VsphereCounterChartData = {}

        // Filter chartsDataPerServer by server filter or use all servers
        let filteredChartsDataPerServer: Record<string, VsphereCounterChartData> = {}
        if (server_ids.length > 0) {
            for (const server_id of server_ids) {
                if (state.chartsDataPerServer[server_id]) {
                    filteredChartsDataPerServer[server_id] = state.chartsDataPerServer[server_id]
                }
            }
        } else {
            filteredChartsDataPerServer = state.chartsDataPerServer
        }
        counterChartsData = this.mergeCountersByDate(filteredChartsDataPerServer)

        for (const [dateString, charts_data] of Object.entries(counterChartsData)) {
            let formattedDate = this.dateService.formatLongDate(dateString)
            let compliantCount = 0
            let notCompliantCount = 0
            for (const type in charts_data) {
                if (types.includes(type)) {
                    const typeMeasures = charts_data[type];
                    compliantCount += typeMeasures.checked;
                    notCompliantCount += typeMeasures.unchecked;
                }
            }
            complianceRatioChartValues.push({
                "dateString": formattedDate,
                "date": new Date(formattedDate),
                "value": CompliantRatioService.calculateCompliantRatio(compliantCount, notCompliantCount)
            });
            complianceTotalChartValues.push({
                "dateString": formattedDate,
                "date": new Date(formattedDate),
                "compliantCount": compliantCount,
                "notCompliantCount": notCompliantCount,
                "total": compliantCount + notCompliantCount,
                "delta": Math.abs(compliantCount - notCompliantCount)
            });
        }
        let lastDate = Object.keys(counterChartsData)[Object.keys(counterChartsData).length - 1]
        let lastMeasure = counterChartsData[lastDate]
        for (const type in lastMeasure) {
            if (types.includes(type)) {
                let typeMeasure = lastMeasure[type]
                let colorKey = 'charts_color_' + complianceVersusChartValues.length
                let dateData = {
                    "type": this.vsphereResourceTypeLabelPipe.transform(type),
                    "count": typeMeasure.checked + typeMeasure.unchecked + typeMeasure.ignored,
                    "color": ColorEnum[colorKey as keyof typeof ColorEnum]
                };
                complianceVersusChartValues.push(dateData);
            }
        }
        return ctx.patchState(
            {
                chartData: {
                    filteredComplianceRatioChartData: {
                        values: complianceRatioChartValues
                    },
                    filteredComplianceTotalChartData: {
                        values: complianceTotalChartValues
                    },
                    filteredVersusChartData: {
                        values: complianceVersusChartValues
                    }
                }
            }
        )
    }
}
